import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box, TextField, Typography } from '@mui/material';

import { automationApi } from './api/automationApi';
import AutomationList from './components/AutomationList';
import CreateAutomationModal from './components/CreateAutomationModal';
import { useAutomation } from './hooks/useAutomation';
import { useAutomationDefinitions } from './hooks/useAutomationDefinitions';
import { debounce } from './utils/automationUtils';
import InternalButton from '../../components/InternalButton';
import Spin from '../../components/Spin';
import { useThemeContext } from '../../global/ThemeContext/ThemeContext';
import GlobalModal from 'modals/GlobalModal/GlobalModal';

const AutomationV2: React.FC = () => {
  const theme = useThemeContext();

  // regras
  const { rules, loading: rulesLoading, error: rulesError, reload } = useAutomation();

  // definitions dinâmicas
  const { pluginFor, loading: defsLoading, error: defsError } = useAutomationDefinitions();
  const formPlugin = pluginFor?.('form_submission');

  // organização do usuário
  const organizationUuid = useMemo(() => localStorage.getItem('x-organization-uuid') || '', []);

  const [searchTerm, setSearchTerm] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);

  const [editRuleUuid, setEditRuleUuid] = React.useState<string | null>(null);

  const debouncedSearch = debounce((term: string) => setSearchTerm(term), 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const filteredRules = rules.filter(
    rule =>
      rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rule.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const handleDeleteRule = async (uuid: string) => {
    setRuleToDelete(uuid);
    setDeleteConfirmationModal(true);
  };

  const confirmDelete = async () => {
    if (ruleToDelete) {
      const success = await automationApi.deleteRule(ruleToDelete);
      if (success) {
        await reload();
      }
    }
    setDeleteConfirmationModal(false);
    setRuleToDelete(null);
    await reload();
  };

  const cancelDelete = () => {
    setDeleteConfirmationModal(false);
    setRuleToDelete(null);
  };

  const handleOpenCreate = () => {
    console.log('🔍 handleOpenCreate - Limpando editRuleUuid');
    setEditRuleUuid(null);
    setOpenModal(true);
  };

  const handleEdit = (uuid: string) => {
    console.log('🔍 handleEdit - Setando editRuleUuid:', uuid);
    setEditRuleUuid(uuid);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    console.log('🔍 handleCloseModal - Fechando modal e limpando editRuleUuid');
    setOpenModal(false);
    setEditRuleUuid(null);
  };

  const isLoading = Boolean(rulesLoading || defsLoading);

  const errorObj = rulesError ?? defsError;
  const hasError = Boolean(errorObj);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', my: 0, padding: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: '0.5rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: '0.6',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h5" sx={{ color: theme.text.primary, fontWeight: 600 }}>
            Automação ({filteredRules.length})
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '0.75rem',
            flex: '0.45',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Buscar automação"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: searchTerm === '' ? <SearchIcon /> : null,
              sx: { maxHeight: 28 },
            }}
          />
          <InternalButton
            size="large"
            background={theme.primary}
            variant="primary"
            iconPosition="left"
            icon={<AddIcon sx={{ fontSize: 'auto' }} />}
            onClick={handleOpenCreate}
            disabled={isLoading}
          >
            Criar Regra de Automação
          </InternalButton>
        </Box>
      </Box>

      {hasError && (
        <Box
          sx={{
            backgroundColor: theme.error,
            color: 'white',
            p: '0.75rem',
            borderRadius: '0.5rem',
            mb: '0.5rem',
          }}
        >
          <Typography sx={{ color: 'white' }}>{errorObj ? String(errorObj) : ''}</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          flexDirection: 'column',
          minHeight: '300px',
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Spin />
          </Box>
        ) : filteredRules.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography sx={{ color: 'gray' }}>
              {searchTerm
                ? 'Nenhuma automação encontrada com o termo buscado.'
                : 'Nenhuma automação cadastrada ainda.'}
            </Typography>
          </Box>
        ) : (
          <AutomationList
            automations={filteredRules}
            loading={isLoading}
            onUpdate={reload}
            onDelete={handleDeleteRule}
            onEdit={handleEdit}
          />
        )}
        {formPlugin && (
          <CreateAutomationModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            organizationUuid={organizationUuid}
            plugin={formPlugin}
            editRuleUuid={editRuleUuid ?? undefined} // << se vier, é edição
            onCreated={async () => {
              await reload(); // recarrega lista ao concluir
            }}
          />
        )}
        <GlobalModal
          open={deleteConfirmationModal}
          title="Confirmação"
          description="Deseja realmente excluir a automação?"
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmButtonText="Sim"
          cancelButtonText="Não"
          width="30rem"
          height="15rem"
          closeOnBackdropClick={false}
          closeOnEscapeKeyDown={false}
        />{' '}
      </Box>
    </Box>
  );
};

export default AutomationV2;
